import type { ThemeConfig, ThemeToken, ThemeTranslations } from "./theme.type";

import en_US from "antd/locale/en_US";
import fr_CA from "antd/locale/fr_CA";

import { THEME_BREAKPOINTS_MIN } from "./theme-breakpoints/theme-breakpoints.constant";
import {
  THEME_COLORS,
  colorPrimary,
  colorHighlight,
  colorText,
  colorBodyBg,
  colorBlack,
  colorAccent,
  generateCustomColors,
  colorBorder,
  colorBorderSecondary,
} from "./theme-colors";
import { THEME_COMPONENTS } from "./theme-components";
import { THEME_Z_INDEX } from "./theme-z-index";

export const THEME_TOKEN: ThemeToken = {
  // --- AntDesign overridden variables
  colorPrimary,
  colorPrimaryBg: THEME_COLORS.primary[1],
  colorPrimaryBgHover: THEME_COLORS.primary[2],
  colorTextBase: colorText,
  colorTextHeading: colorText,
  colorTextPlaceholder: THEME_COLORS.grey[3],
  colorLink: colorPrimary,
  colorLinkHover: colorAccent,
  colorBorder,
  colorBorderSecondary,
  colorHighlight,
  colorInfo: THEME_COLORS.status.info,
  colorSuccess: THEME_COLORS.status.success,
  colorWarning: THEME_COLORS.status.warning,
  colorError: THEME_COLORS.status.error,
  fontFamily: "Roboto, sans-serif",
  fontSize: 14,
  fontWeightStrong: 700,
  borderRadius: 2,

  /**
   * These values align with Ant Design's internal implementation
   * https://github.com/ant-design/ant-design/blob/0530d5685dad0e48bec70e5f379eb7c144fae099/components/grid/hooks/useBreakpoint.tsx
   * https://github.com/ant-design/ant-design/blob/0530d5685dad0e48bec70e5f379eb7c144fae099/components/theme/util/alias.ts#L28
   */
  screenXS: 480, // Ant Design's internal reference point
  screenXSMin: 0,
  screenXSMax: THEME_BREAKPOINTS_MIN.sm - 1,

  screenSM: THEME_BREAKPOINTS_MIN.sm,
  screenSMMin: THEME_BREAKPOINTS_MIN.sm,
  screenSMMax: THEME_BREAKPOINTS_MIN.md - 1,

  screenMD: THEME_BREAKPOINTS_MIN.md,
  screenMDMin: THEME_BREAKPOINTS_MIN.md,
  screenMDMax: THEME_BREAKPOINTS_MIN.lg - 1,

  screenLG: THEME_BREAKPOINTS_MIN.lg,
  screenLGMin: THEME_BREAKPOINTS_MIN.lg,
  screenLGMax: THEME_BREAKPOINTS_MIN.xl - 1,

  screenXL: THEME_BREAKPOINTS_MIN.xl,
  screenXLMin: THEME_BREAKPOINTS_MIN.xl,
  screenXLMax: THEME_BREAKPOINTS_MIN.xxl - 1,

  screenXXL: THEME_BREAKPOINTS_MIN.xxl,
  screenXXLMin: THEME_BREAKPOINTS_MIN.xxl,
  screenXXLMax: THEME_BREAKPOINTS_MIN.xxxl - 1,

  screenXXXL: THEME_BREAKPOINTS_MIN.xxxl,
  screenXXXLMin: THEME_BREAKPOINTS_MIN.xxxl,

  // --- Stelar custom colors & variables
  customColors: generateCustomColors(THEME_COLORS),
  customToken: {
    fontWeightBase: 400,
    fontWeightMedium: 500,
    fontWeightHeading: 500,
    bodyBg: colorBodyBg,
    cardTitleFontSize: "16px",
    cardSecondaryTitleFontSize: "12px",
    layoutContentPadding: "34px",
    layoutBoxShadow: "1px 0px 10px rgba(0,0,0,.15)",
    zIndexLayoutShell: THEME_Z_INDEX.layoutShell,
    zIndexPopup: THEME_Z_INDEX.popup,
    colorBlack,
    colorAccent,
    // expose breakpoints to be used in less file if necessary
    breakpointXs: THEME_BREAKPOINTS_MIN.xs,
    breakpointSm: THEME_BREAKPOINTS_MIN.sm,
    breakpointMd: THEME_BREAKPOINTS_MIN.md,
    breakpointLg: THEME_BREAKPOINTS_MIN.lg,
    breakpointXl: THEME_BREAKPOINTS_MIN.xl,
    breakpointXxl: THEME_BREAKPOINTS_MIN.xxl,
    breakpointXxxl: THEME_BREAKPOINTS_MIN.xxxl,
  },
};

export const THEME_CLASSNAME_PREFIX = "ant";
export const THEME_CSSVAR_PREFIX = "app";
export const THEME_KEY = "stelar";

export const THEME_TRANSLATIONS: ThemeTranslations = {
  en: en_US,
  fr: fr_CA,
  default: en_US,
};

// To test theme render with custom values for token and
// components, go to https://ant.design/theme-editor
export const THEME_CONFIG: ThemeConfig = {
  cssVar: {
    key: THEME_KEY,
    prefix: THEME_CSSVAR_PREFIX,
  },
  token: THEME_TOKEN,
  components: THEME_COMPONENTS,
};

export const ANT_DESIGN_TOKEN_KEYS_TO_REMOVE = [
  "blue",
  "cyan",
  "geekblue",
  "gold",
  "green",
  "lime",
  "magenta",
  "orange",
  "pink",
  "purple",
  "red",
  "volcano",
  "yellow",
];
